import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Send, Wind, Volume2 } from "lucide-react";
import { TalkingPortrait } from "../../components/coach/TalkingPortrait";
import { CreateHelpChat, CreateHelpCard } from "./CreateHelpChat";
import { ListeningNights } from "../../components/soulumoCreate/NightStart";
import { areaFromPath, coachThemeVars, useCoachTheme } from "../../components/coach/coachTheme";

const API = process.env.REACT_APP_BACKEND_URL;
const SERIF = '"Cormorant Garamond", Georgia, serif';
const SANS = '"Manrope", sans-serif';
const C = { bg: "var(--coach-bg, #0B1411)", cream: "#F1ECDD", dim: "rgba(241,236,221,0.62)", gold: "#D7BB7A", line: "rgba(215,187,122,0.22)" };
const glass = { background: "rgba(241,236,221,0.05)", border: `1px solid ${C.line}`, backdropFilter: "blur(14px)" };

const track = (area, type, id = "") => axios.post(`${API}/api/guide/${area}/event`, { type, id }).catch(() => {});
const dayKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
// Consecutive days (ending today or yesterday) this device did the check-in.
export const checkinStreak = (sess) => {
  const days = new Set(JSON.parse(localStorage.getItem(`checkin_days_${sess}`) || "[]"));
  const d = new Date();
  if (!days.has(dayKey(d))) d.setDate(d.getDate() - 1);
  let n = 0;
  while (days.has(dayKey(d))) { n += 1; d.setDate(d.getDate() - 1); }
  return n;
};
const localDays = (sess) => JSON.parse(localStorage.getItem(`checkin_days_${sess}`) || "[]");
// Signed in: merge this device's days with the account (union, so the longest streak is kept everywhere).
export const syncCheckins = async () => {
  const tok = localStorage.getItem("reclaim_token");
  if (!tok) return false;
  try {
    const r = await axios.post(`${API}/api/guide-streaks/sync`, { morning: localDays("morning"), evening: localDays("evening") }, { headers: { Authorization: `Bearer ${tok}` } });
    ["morning", "evening"].forEach((k) => localStorage.setItem(`checkin_days_${k}`, JSON.stringify(r.data[k] || [])));
    return true;
  } catch { return false; }
};
const markCheckin = (sess) => {
  const days = localDays(sess);
  const t = dayKey(new Date());
  if (!days.includes(t)) localStorage.setItem(`checkin_days_${sess}`, JSON.stringify([...days, t].slice(-90)));
  syncCheckins();
};
const nowSession = () => (new Date().getHours() < 15 ? "morning" : "evening");

// No AI: a typed question is matched to the nearest ready-made answer.
export const match = (text, faqs) => {
  const t = ` ${text.toLowerCase().replace(/[^a-z0-9 ]/g, " ")} `;
  let best = null; let score = 0;
  faqs.forEach((f) => {
    let s = f.k.reduce((n, k) => n + (t.includes(` ${k} `) || (k.includes(" ") && t.includes(k)) ? 2 : 0), 0);
    s += f.q.toLowerCase().split(/\W+/).filter((w) => w.length > 3 && t.includes(` ${w} `)).length;
    if (s > score) { score = s; best = f; }
  });
  return score >= 2 ? best : null;
};

const Chooser = ({ ids, onPick }) => (
  <div className="mt-10 grid grid-cols-2 gap-4" data-testid="guide-lite-chooser">
    {Object.entries(ids).map(([k, v]) => (
      <button key={k} type="button" onClick={() => onPick(k)} data-testid={`guide-lite-choose-${k}`} className="rounded-3xl overflow-hidden text-left transition-transform duration-300 hover:-translate-y-1" style={glass}>
        <img src={v.image} alt={v.name} className="w-full aspect-[3/4] object-cover object-top" />
        <span className="block px-4 py-3 text-xl" style={{ fontFamily: SERIF, color: C.cream }}>{v.name}</span>
      </button>
    ))}
  </div>
);

// Ready-made guide: spoken answers about the app, links into it, and one-minute resets.
export default function GuideLitePage({ area }) {
  useCoachTheme(area, useEffect);
  const nav = useNavigate();
  const audio = useRef(null);
  const [g, setG] = useState(null);
  const [who, setWho] = useState(() => localStorage.getItem("guide_lite_who"));
  const [env, setEnv] = useState(null);
  const [speaking, setSpeaking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [answer, setAnswer] = useState(null);
  const [line, setLine] = useState("");
  const [q, setQ] = useState("");
  const [script, setScript] = useState(null);
  const [params] = useSearchParams();
  const [flow, setFlow] = useState(null); // morning check-in: greet → breathe → ask → mood
  const flowRef = useRef(null);
  flowRef.current = flow;

  useEffect(() => { axios.get(`${API}/api/guide/${area}`).then((r) => setG(r.data)).catch(() => {}); track(area, "open"); }, [area]);
  const qs = params.get("checkin");
  const linkSess = qs === "evening" ? "evening" : qs ? "morning" : null;
  const [sess, setSess] = useState(linkSess || nowSession());
  const ck = g?.checkins?.[sess];
  const intro = params.get("intro") === "1";
  const [helping, setHelping] = useState(false);
  const [first, setFirst] = useState(null);
  const [needTap, setNeedTap] = useState(false);
  const autoBegin = useRef(false);
  const [locked, setLocked] = useState(false);
  useEffect(() => {
    if (area !== "soulumo") return;
    if (!localStorage.getItem("reclaim_token")) { setFirst(true); return; }
    axios.get(`${API}/api/audio-journeys`, { headers: { Authorization: `Bearer ${localStorage.getItem("reclaim_token")}` } })
      .then((r) => setFirst(!((r.data?.items || []).length))).catch(() => {});
  }, [area]);
  const startHelp = async () => {
    const tok = localStorage.getItem("reclaim_token");
    if (!tok) { nav("/soulumo/join?mode=signin"); return; }
    const ok = await axios.get(`${API}/api/soulumo/create-chat/access`, { headers: { Authorization: `Bearer ${tok}` } }).then((r) => r.data.allowed).catch(() => false);
    if (!ok) { nav("/soulumo/demo"); return; }
    track(area, "intro", "create_help"); setHelping(true);
  };
  const wantHelp = area === "soulumo" && params.get("help") === "1";
  useEffect(() => { if (wantHelp && g && who) startHelp(); }, [wantHelp, !!g, who]); // eslint-disable-line react-hooks/exhaustive-deps

  const play = (url, e, scr = null) => {
    const a = audio.current;
    if (!url) { a.pause(); return; }
    setEnv(e || null); setScript(scr); setLine("");
    a.src = `${API}${url}`;
    a.play().catch((err) => { setSpeaking(false); if (err?.name === "NotAllowedError") setNeedTap(true); });
  };
  const say = async (id, shown) => {
    setBusy(true); setAnswer(shown);
    try { const r = await axios.post(`${API}/api/guide/${area}/say`, { who, id }); play(r.data.audio_url, r.data.env); } catch { /* text still shows */ }
    setBusy(false);
  };
  const ask = (text) => {
    const f = match(text, g.faqs);
    setQ(""); setFlow(null);
    track(area, f ? "typed" : "miss", f ? f.id : "");
    if (!f) axios.post(`${API}/api/guide/${area}/miss`, { text }).catch(() => {});
    say(f ? f.id : "fallback", f ? { ...f, asked: text } : { id: "fallback", a: g.fallback, asked: text });
  };
  const reset = async (id) => {
    setBusy(true); setAnswer(null); track(area, "reset", id);
    try { const r = await axios.get(`${API}/api/guide/${area}/reset/${id}?who=${who}`); play(r.data.audio_url, r.data.env, r.data.script || []); } catch { /* unavailable */ }
    setBusy(false);
  };
  const startCheckin = (which = sess) => {
    setSess(which); setFlow("greet"); markCheckin(which); track(area, `checkin_${which}`);
    say(`checkin_${which}_greet`, { id: "checkin", a: g.checkins[which].greet });
  };
  const ended = () => {
    setSpeaking(false); setScript(null); setLine("");
    if (flowRef.current === "greet") { setFlow("breathe"); reset(ck.reset || "breathe"); }
    else if (flowRef.current === "breathe") { setFlow("ask"); say(`checkin_${sess}_ask`, { id: "checkin_ask", a: ck.ask }); }
  };
  const mood = (m) => { setFlow("mood"); track(area, "mood", `${sess}_${m.id}`); say(`mood_${sess}_${m.id}`, { id: m.id, a: m.a, link: m.link, asked: m.label }); };
  const pick = (k) => { localStorage.setItem("guide_lite_who", k); setWho(k); };
  const onTime = () => {
    if (!script) return;
    const t = audio.current.currentTime;
    const cur = [...script].reverse().find((s) => s.start <= t);
    setLine(cur ? cur.text : "");
  };

  const begin = () => {
    if (ck && linkSess) return startCheckin(linkSess);
    const what = intro && g.faqs.find((f) => f.id === "what");
    if (what) { track(area, "intro"); return say("what", what); }
    return say("welcome", { id: "welcome", a: g.welcome });
  };
  const person = g && who && g.identities[who];
  // The guide speaks on its own as soon as it's on screen.
  useEffect(() => {
    if (!person || autoBegin.current || wantHelp) return;
    autoBegin.current = true; begin();
  }, [!!person]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!g) return <div className="min-h-screen" style={{ background: C.bg }} />;
  return (
    <div className="min-h-screen px-5 pt-8 pb-24" style={{ background: `radial-gradient(ellipse at 50% 0%, var(--coach-glow) 0%, ${C.bg} 60%)`, color: C.cream }} data-testid={`guide-lite-${area}`}>
      <audio ref={audio} onPlay={() => setSpeaking(true)} onEnded={ended} onPause={() => setSpeaking(false)} onTimeUpdate={onTime} />
      <div className="max-w-md mx-auto">
        <button type="button" onClick={() => nav(g.home)} aria-label="Back" data-testid="guide-lite-back" style={{ color: C.cream }}><ArrowLeft size={20} /></button>
        <p className="mt-6 text-center text-[0.62rem] uppercase tracking-[0.34em]" style={{ color: C.gold, fontFamily: SANS }}>{g.title} · your guide</p>
        {!person ? (
          <>
            <h1 className="mt-4 text-center text-4xl" style={{ fontFamily: SERIF, color: C.cream }}>Who would you like to guide you?</h1>
            <Chooser ids={g.identities} onPick={pick} />
          </>
        ) : (
          <>
            <div className="mt-6 mx-auto w-48 h-48 rounded-full overflow-hidden" style={{ boxShadow: `0 0 0 1px ${C.line}, 0 30px 80px -30px rgba(215,187,122,0.45)` }}>
              <TalkingPortrait who={person} audioEl={audio.current} env={env} speaking={speaking} status={busy ? "thinking" : "idle"} />
            </div>
            <p className="mt-4 text-center text-2xl" style={{ fontFamily: SERIF, color: C.cream }}>{person.name}</p>
            {needTap && (
              <button type="button" onClick={() => { setNeedTap(false); audio.current.play().catch(() => {}); }} data-testid="guide-lite-tap-start"
                className="mt-3 mx-auto flex items-center gap-2 rounded-full px-5 py-2.5 text-sm transition-transform duration-200 active:scale-95" style={{ background: C.gold, color: C.bg }}>
                <Volume2 size={15} /> Tap once to hear {person.name}
              </button>
            )}
            <button type="button" onClick={() => pick(who === "adelaida" ? "richard" : "adelaida")} className="block mx-auto text-xs underline" style={{ color: C.dim }} data-testid="guide-lite-switch">Switch guide</button>

            {area === "soulumo" && locked && (
              <div className="mt-6 rounded-3xl p-5 text-center" style={glass} data-testid="guide-lite-members-only">
                <p className="text-xl leading-snug" style={{ fontFamily: SERIF, color: C.cream }}>Talking it through with {person.name} comes with Soulumo membership.</p>
                <p className="mt-2 text-sm" style={{ color: C.dim, fontFamily: SANS }}>For now, just type what's on your mind. Soulumo will shape it into your installation beautifully.</p>
                <button type="button" onClick={() => nav("/my-journeys")} data-testid="guide-lite-members-type"
                  className="mt-4 w-full rounded-full py-3 text-sm uppercase tracking-[0.2em] transition-transform duration-200 active:scale-95" style={{ background: C.gold, color: C.bg }}>Type it instead</button>
                <button type="button" onClick={() => nav("/voice-clone")} data-testid="guide-lite-members-join" className="mt-3 text-xs underline" style={{ color: C.dim }}>See membership</button>
              </div>
            )}
            {area === "soulumo" && !helping && !locked && <CreateHelpCard onYes={startHelp} first={first} />}
            {helping && <CreateHelpChat who={who} name={person.name} play={play} speaking={speaking} onEnd={() => setHelping(false)} />}
            {!helping && <>
            <div className="mt-6 rounded-3xl p-5 min-h-[6rem]" style={glass} data-testid="guide-lite-answer">
              {script ? (
                <p className="text-xl italic text-center" style={{ fontFamily: SERIF, color: C.cream }} data-testid="guide-lite-reset-line">{line || "…"}</p>
              ) : answer ? (
                <>
                  {answer.asked && <p className="text-xs" style={{ color: C.dim }} data-testid="guide-lite-asked">“{answer.asked}”</p>}
                  <p className="mt-1 text-lg leading-relaxed" style={{ fontFamily: SERIF, color: C.cream }} data-testid="guide-lite-answer-text">{answer.a}</p>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {answer.link && <button type="button" onClick={() => nav(answer.link.route)} data-testid="guide-lite-answer-link" className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm" style={{ background: C.gold, color: C.bg }}>{answer.link.label} <ArrowRight size={14} /></button>}
                    {answer.reset && <button type="button" onClick={() => reset(answer.reset)} data-testid="guide-lite-answer-reset" className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm" style={{ border: `1px solid ${C.gold}`, color: C.gold }}><Wind size={14} /> Try it now</button>}
                  </div>
                </>
              ) : (
                <button type="button" data-testid="guide-lite-begin" className="w-full text-center text-lg italic" style={{ fontFamily: SERIF, color: C.gold }}
                  onClick={begin}>
                  {ck && linkSess ? `Tap to begin your ${linkSess} check-in` : intro ? `Tap to hear what ${g.title} is` : `Tap to hear ${person.name}`}
                </button>
              )}
              {flow === "ask" && !speaking && (
                <div className="mt-4 flex flex-wrap gap-2" data-testid="guide-lite-moods">
                  {ck.moods.map((m) => (
                    <button key={m.id} type="button" onClick={() => mood(m)} data-testid={`guide-lite-mood-${m.id}`} className="rounded-full px-4 py-2 text-sm" style={{ border: `1px solid ${C.gold}`, color: C.gold }}>{m.label}</button>
                  ))}
                </div>
              )}
            </div>

            {ck && !flow && !linkSess && (
              <button type="button" onClick={() => startCheckin(nowSession())} data-testid="guide-lite-checkin" className="mt-4 w-full rounded-full py-3 text-sm inline-flex items-center justify-center gap-2" style={{ ...glass, color: C.gold }}>
                <Wind size={14} /> One-minute {nowSession()} check-in
              </button>
            )}
            <form className="mt-4 flex gap-2" onSubmit={(e) => { e.preventDefault(); if (q.trim()) ask(q.trim()); }}>
              <input value={q} onChange={(e) => setQ(e.target.value)} maxLength={200} placeholder={`Ask ${person.name} about ${g.title}…`} data-testid="guide-lite-input"
                className="flex-1 rounded-full px-5 py-3 outline-none text-sm" style={{ ...glass, color: C.cream }} />
              <button type="submit" disabled={!q.trim() || busy} aria-label="Ask" data-testid="guide-lite-ask" className="rounded-full w-12 flex items-center justify-center disabled:opacity-40" style={{ background: C.gold, color: C.bg }}><Send size={16} /></button>
            </form>

            <div className="mt-6 flex flex-wrap gap-2" data-testid="guide-lite-questions">
              {g.faqs.map((f) => (
                <button key={f.id} type="button" onClick={() => { track(area, "question", f.id); say(f.id, f); }} data-testid={`guide-lite-q-${f.id}`} className="rounded-full px-4 py-2 text-sm transition-colors duration-200 hover:bg-white/10" style={{ ...glass, color: C.cream }}>{f.q}</button>
              ))}
            </div>

            </>}
            <p className="mt-10 text-[0.62rem] uppercase tracking-[0.3em]" style={{ color: C.gold, fontFamily: SANS }}>One-minute resets</p>
            <div className="mt-3 grid grid-cols-2 gap-3" data-testid="guide-lite-resets">
              {g.resets.map((r) => (
                <button key={r.id} type="button" onClick={() => reset(r.id)} data-testid={`guide-lite-reset-${r.id}`} className="rounded-2xl p-4 text-left transition-transform duration-200 hover:-translate-y-0.5" style={glass}>
                  <span className="block text-lg" style={{ fontFamily: SERIF }}>{r.name}</span>
                  <span className="block text-xs mt-1" style={{ color: C.dim }}>{r.when}</span>
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// Soulumo: members get the full AI guide; everyone else gets the ready-made one.
export const SoulumoGuidePage = ({ Full }) => {
  const [ok, setOk] = useState(null);
  useEffect(() => {
    if (!localStorage.getItem("reclaim_token")) { setOk(false); return; }
    axios.get(`${API}/api/coach/soulumo/state`, { headers: { Authorization: `Bearer ${localStorage.getItem("reclaim_token")}` } })
      .then((r) => setOk(!!r.data.eligible)).catch(() => setOk(false));
  }, []);
  if (ok === null) return <div className="min-h-screen" style={{ background: C.bg }} />;
  return ok ? <Full coach="soulumo" /> : <GuideLitePage area="soulumo" />;
};

// Entry card for the ready-made guides (always shown; no sign-in needed).
export const GuideLiteEntry = ({ to, label }) => (
  <button type="button" onClick={() => { window.location.href = to; }} data-testid={`guide-lite-entry-${label.toLowerCase().replace(/\s+/g, "-")}`}
    className="w-full rounded-3xl p-4 flex items-center gap-4 text-left transition-transform duration-300 hover:-translate-y-0.5"
    style={{ ...coachThemeVars(areaFromPath(to)), background: "var(--coach-card)", border: `1px solid ${C.line}` }}>
    <span className="flex -space-x-3 shrink-0">
      {["adelaida", "richard"].map((k) => <img key={k} src={`/coaches/${k}_calm.webp`} alt={k} className="w-12 h-12 rounded-full object-cover object-top" style={{ boxShadow: `0 0 0 2px ${C.bg}` }} />)}
    </span>
    <span className="min-w-0 flex-1">
      <span className="block text-[0.55rem] uppercase tracking-[0.28em]" style={{ color: C.gold, fontFamily: SANS }}>{label} guide</span>
      <span className="block mt-1 text-xl leading-tight" style={{ fontFamily: SERIF, color: C.cream }}>Questions? Ask your guide</span>
    </span>
    <ArrowRight size={18} style={{ color: C.gold }} />
  </button>
);

// Always-visible guide button for the free areas; on a first visit it can offer to explain the area.
export const GuideBubble = ({ to, label, introKey = null, testId = "guide-bubble", compact = false }) => {
  const who = localStorage.getItem("guide_lite_who") || "adelaida";
  const [offer, setOffer] = useState(() => !!introKey && !localStorage.getItem(introKey));
  useEffect(() => {
    if (!offer) return undefined;
    const t = setTimeout(() => setOffer("show"), 2500);
    return () => clearTimeout(t);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const dismiss = () => { localStorage.setItem(introKey, "1"); setOffer(false); };
  const nav = useNavigate();
  const go = (u) => { if (introKey) localStorage.setItem(introKey, "1"); nav(u); };
  const tv = coachThemeVars(areaFromPath(to));
  const name = who === "richard" ? "Richard" : "Adelaida";
  return (
    <>
      {offer === "show" && (
        <div className="fixed right-4 z-[61] max-w-[17rem] rounded-2xl p-4" data-testid="guide-bubble-intro"
          style={{ bottom: "calc(env(safe-area-inset-bottom, 0px) + 150px)", ...tv, background: "var(--coach-deep)", border: `1px solid ${C.gold}`, backdropFilter: "blur(14px)" }}>
          <p className="text-base leading-snug" style={{ fontFamily: SERIF, color: C.cream }}>Hello, I'm {name}. Shall I explain what {label} is?</p>
          <div className="mt-3 flex gap-2">
            <button type="button" onClick={() => go(`${to}?intro=1`)} data-testid="guide-bubble-intro-yes" className="rounded-full px-4 py-1.5 text-xs font-semibold" style={{ background: C.gold, color: C.bg }}>Yes please</button>
            <button type="button" onClick={dismiss} data-testid="guide-bubble-intro-no" className="rounded-full px-4 py-1.5 text-xs" style={{ color: C.dim }}>Not now</button>
          </div>
        </div>
      )}
      <button type="button" onClick={() => go(to)} aria-label={`Ask your ${label} guide`} data-testid={testId}
        className={`fixed right-4 z-[60] flex items-center gap-2 rounded-full transition-transform duration-200 hover:-translate-y-0.5 ${compact ? "p-1" : "pl-1 pr-4 py-1"}`}
        style={{ bottom: "calc(env(safe-area-inset-bottom, 0px) + 88px)", ...tv, background: "var(--coach-deep)", border: `1px solid ${C.gold}`, backdropFilter: "blur(12px)", boxShadow: "0 12px 40px -12px rgba(0,0,0,0.6)" }}>
        <img src={`/coaches/${who}_calm.webp`} alt="" className="w-11 h-11 rounded-full object-cover object-top" />
        {!compact && <span className="text-xs uppercase tracking-[0.2em]" style={{ color: C.gold, fontFamily: SANS }}>Guide</span>}
      </button>
    </>
  );
};

// Shown at the top of Foundations in the morning and evening: one free, pre-recorded minute.
export const CheckinCard = ({ session }) => {
  const who = localStorage.getItem("guide_lite_who") || "adelaida";
  const [streak, setStreak] = useState(() => checkinStreak(session));
  useEffect(() => { syncCheckins().then((ok) => ok && setStreak(checkinStreak(session))); }, [session]);
  const unit = session === "morning" ? "morning" : "evening";
  return (
    <button type="button" onClick={() => { window.location.href = `/foundations/guide?checkin=${session}`; }} data-testid={`foundations-${session}-checkin`}
      className="w-full rounded-3xl p-4 flex items-center gap-4 text-left transition-transform duration-300 hover:-translate-y-0.5"
      style={{ ...coachThemeVars(areaFromPath(window.location.pathname)), background: "var(--coach-card-gold)", border: `1px solid ${C.gold}` }}>
      <img src={`/coaches/${who}_calm.webp`} alt="" className="w-16 h-16 rounded-full object-cover object-top shrink-0" style={{ boxShadow: `0 0 0 2px ${C.gold}` }} />
      <span className="min-w-0 flex-1">
        <span className="block text-[0.55rem] uppercase tracking-[0.28em]" style={{ color: C.gold, fontFamily: SANS }}>{session === "morning" ? "Good morning" : "Good evening"} · 1 minute</span>
        <span className="block mt-1 text-xl leading-tight" style={{ fontFamily: SERIF, color: C.cream }}>Your {unit} check-in</span>
        <span className="block mt-1 text-xs" style={{ color: C.dim }} data-testid={`foundations-${session}-streak`}>
          {streak > 1 ? `${streak} ${unit}s in a row. Lovely.` : streak === 1 ? `You checked in ${session === "morning" ? "this morning" : "tonight"}. Come back tomorrow.` : session === "morning" ? "A few slow breaths, then how you're arriving today." : "Let the day go, then how you're settling tonight."}
        </span>
        <ListeningNights className="mt-1 text-xs" style={{ color: C.gold }} />
      </span>
      <ArrowRight size={18} style={{ color: C.gold }} />
    </button>
  );
};
